import {
  CONSENT_COOKIE,
  CONSENT_VERSION,
  DEFAULT_DENIED,
  type ConsentCategories,
  type StoredConsent,
} from './types'

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return
  const oneYear = 60 * 60 * 24 * 365
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:'
  const secure = isHttps ? ';secure' : ''
  document.cookie = `${name}=${encodeURIComponent(value)};max-age=${oneYear};path=/;samesite=lax${secure}`
}

// Safari/WebKit's ITP caps any cookie written via document.cookie (as
// opposed to a server Set-Cookie header) to a 7-day expiry, no matter what
// Max-Age is requested -- so a Safari visitor who accepts/rejects cookies
// gets silently re-prompted about a week later even though this asks for a
// year. localStorage has no such cap, so it's the source of truth here;
// the cookie is kept as a secondary write in case anything server-side
// ever wants to read it, but reads always prefer localStorage.
function readLocalStorage(name: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(name)
  } catch {
    return null // private-mode / storage disabled -- caller falls back to the cookie
  }
}

function writeLocalStorage(name: string, value: string) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(name, value)
  } catch {
    /* private-mode / quota exceeded -- the cookie write is the fallback */
  }
}

export function readStoredConsent(): StoredConsent | null {
  const raw = readLocalStorage(CONSENT_COOKIE) ?? readCookie(CONSENT_COOKIE)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent> & {
      categories?: Partial<ConsentCategories>
    }
    if (!parsed || typeof parsed !== 'object') return null
    // Accept v1 cookies (no `functional` field) and migrate in memory —
    // we'll persist the v2 shape on the next write the user triggers.
    const storedVersion = typeof parsed.version === 'number' ? parsed.version : 0
    if (storedVersion !== 1 && storedVersion !== CONSENT_VERSION) return null
    const cats = parsed.categories
    if (!cats || typeof cats !== 'object') return null
    const normalized: ConsentCategories = {
      necessary: true,
      analytics: Boolean(cats.analytics),
      marketing: Boolean(cats.marketing),
      personalization: Boolean(cats.personalization),
      // v1 cookies pre-date the functional category — default to `true`
      // so existing visitors keep their live-chat widget.
      functional:
        storedVersion === 1 && typeof cats.functional === 'undefined'
          ? true
          : Boolean(cats.functional),
    }
    return {
      categories: normalized,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : new Date().toISOString(),
      version: CONSENT_VERSION,
    }
  } catch {
    return null
  }
}

export function writeStoredConsent(categories: ConsentCategories): StoredConsent {
  const record: StoredConsent = {
    categories: { ...DEFAULT_DENIED, ...categories, necessary: true },
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  }
  const serialized = JSON.stringify(record)
  writeLocalStorage(CONSENT_COOKIE, serialized)
  writeCookie(CONSENT_COOKIE, serialized)
  return record
}

export function clearStoredConsent() {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(CONSENT_COOKIE)
    } catch {
      /* ignore */
    }
  }
  if (typeof document === 'undefined') return
  document.cookie = `${CONSENT_COOKIE}=;max-age=0;path=/;samesite=lax`
}
